import { type CSSProperties } from 'react';
import { X } from 'lucide-react';
import { Button, IconButton } from './ui';
import { useTranslation } from '../../lib/i18n';
import { usePersonalSaleCountdown } from '../../lib/use-personal-sale';
import { personalSaleBannerCopy } from '../../lib/personal-sale';
import { useCreditsWidgetState, useCreditsBiFields } from '../../lib/use-credits-widget-state';
import { useDailyCreditsResetLine, widgetStateResetInputs } from '../../lib/use-daily-credits-reset-line';
import { upgradeHref } from '../../lib/upgrade-origin';
import { useLeaveChatOnNavigate } from '../../lib/use-leave-chat';
import { skipChatUrlSyncOnce } from './FloatingChat/use-chat-url';
import { routerHandlesClick, type ClickModifiers } from '../../lib/use-in-app-link';
import { useGenericBi } from '../../lib/use-generic-bi';
import { useBasePath } from '../../lib/use-base-path';
import { resolveCreditsMeter, useCredits } from '../../lib/use-credits';
import { useCreditPlanContext } from '../../lib/use-credit-plan-context';
import styles from './PersonalSaleBanner.module.css';

/**
 * The personal-sale "Limited time welcome offer" banner. Two DISTINCT chromes, both driven by the same
 * data (title/countdown/CTA):
 *
 *  - **Desktop** (Figma 16946-166681 light / 16946-167651 dark): a bordered Surface/Soft bar, one
 *    centred row (title · counter · CTA), an X pinned to the inline-end edge.
 *  - **Mobile-web** (Figma 16946-162783 default / 16946-163593 low/out / 16946-163188 dark, close-button
 *    treatment 16946-164359): a full-bleed edge-to-edge STRIP (no radius, no border — NOT a floating
 *    card, unlike desktop), flush against the very top of the screen. Its content block is CENTRED
 *    within the strip's width, and the **"Close" text button** sits in flow immediately after the
 *    countdown (not the X, and not a separate row) — mobile-web's own choice, not a native-parity one.
 *  - **Desktop on `/packages`** (Figma 16946-167945): a distinct look — a full-VIEWPORT-width BLUE strip
 *    (`--ds-accent-07`) flush at the top with square corners, light ink, and a WHITE
 *    CTA instead of the green one. `onPackages` selects it; AppFrame mounts THIS one differently (a
 *    full-width flow sibling above `<main>`, like the app-download banner) since it spans the rail too.
 *
 * Colours are `--ds-*` tokens that flip on their own. Desktop: Surface/Soft (`--ds-surface-light-gray`)
 * bordered by `--ds-border-light-default`. Mobile: `--sym-bg-elevated` — the SAME role the app-download
 * banner (`AppBanner.module.css`) uses for its own full-bleed top strip (white-on-page in light mode,
 * a clearly lighter grey in dark) — NOT `--ds-surface-standard-bg`, which IS the page's own background
 * (`--field-color`, globals.css), so that token would render the strip invisible against itself. Both
 * chromes ink their text with Text & Icon/Primary (`--ds-text-primary`). The green CTA is the shared
 * accent `Button`, carrying its own accent-brand fill and elevated text in both themes.
 *
 * The per-second `HH:MM:SS` ticks HERE (`usePersonalSaleCountdown`) rather than in the frame, so only the
 * banner re-renders each second. Visibility + the session dismiss are the caller's
 * (`usePersonalSaleBannerVisibility`), which hands down `anchorMs` and `onDismiss`.
 */

const bar: CSSProperties = {
  position: 'relative',
  boxSizing: 'border-box',
  width: '100%',
  height: 56,
  borderRadius: 12,
  border: '1px solid var(--ds-border-light-default)',
  background: 'var(--ds-surface-light-gray)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'auto',
};

const heading: CSSProperties = {
  fontSize: 20,
  lineHeight: '24px',
  fontWeight: 'var(--sym-weight-medium)',
  letterSpacing: '-0.4px',
  color: 'var(--ds-text-primary)',
  whiteSpace: 'nowrap',
};

// The counter, same type — tabular figures so its width is FIXED and the title/CTA/X never shift as the
// digits tick. Both properties, matching the proven ticking-timer pattern (VoiceCallIsland's `.elapsed`):
// `tabular-nums` should enable `tnum` on its own, but pinning the feature too is what actually holds it
// steady across engines / the font's default feature set.
const counter: CSSProperties = { ...heading, fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1' };

// The low/out-credits variant (Figma 16946-168860) leads with a credits-alert title + an offer line +
// the counter, all a size down at 16px so the extra copy fits the same bar. Shared 16/20 base (Figma
// Heading 03), Text & Icon/Primary, tracking -0.32px — then: the leading title goes SemiBold to stand
// out as the alert (a deliberate step up from Figma's Medium, at product request), the offer line is
// Regular 16/24, and the counter stays Medium.
const alertBase: CSSProperties = {
  fontSize: 16,
  lineHeight: '20px',
  fontWeight: 'var(--sym-weight-medium)',
  letterSpacing: '-0.32px',
  color: 'var(--ds-text-primary)',
  whiteSpace: 'nowrap',
};
const alertTitle: CSSProperties = { ...alertBase, fontWeight: 'var(--sym-weight-semibold)' };
const alertReset: CSSProperties = { ...alertBase, fontSize: 14, fontWeight: 'var(--sym-weight-regular)', letterSpacing: '-0.28px' };
const alertOffer: CSSProperties = { ...alertBase, lineHeight: '24px', fontWeight: 'var(--sym-weight-regular)' };
const alertCounter: CSSProperties = { ...alertBase, fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1' };

// Only positions the DS IconButton at the inline-end edge, vertically centred; the button owns its own
// (ghost) box and the icon colour. inset chosen so the 16px glyph lands ~20px from the edge (Figma).
const closeSlot: CSSProperties = {
  position: 'absolute',
  insetInlineEnd: 12,
  top: '50%',
  transform: 'translateY(-50%)',
};

// A full-VIEWPORT-width blue strip (Figma 16946-167945), flush at the top with SQUARE corners all round,
// so the blue fills edge-to-edge and reads as attached to the top edge rather than a floating card. Every colour here is
// mode-INDEPENDENT (the strip is blue in both themes): accent-07 fill, neutral-50 (#F3F3F3) light ink —
// NOT `--ds-text-primary-light`, which flips to a dark ink in dark mode and would vanish on the blue.
const packagesStrip: CSSProperties = {
  position: 'relative',
  boxSizing: 'border-box',
  width: '100%',
  height: 56,
  background: 'var(--ds-accent-07)',
  borderRadius: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'auto',
};
const packagesHeading: CSSProperties = { ...heading, color: 'var(--ds-neutral-50)' };
const packagesCounter: CSSProperties = { ...packagesHeading, fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1' };
const packagesCloseSlot: CSSProperties = { ...closeSlot, insetInlineEnd: 24 };

// A full-bleed STRIP (no radius, no border — see the header note), edge-to-edge and flush with the very
// top of the screen; padding here is the strip's OWN content inset (px12 horizontal, py24/16 vertical),
// not a card's inset-from-the-page margin — the slot itself carries no outer gap/gutter on narrow.
const mobileBarBase: CSSProperties = {
  position: 'relative',
  boxSizing: 'border-box',
  width: '100%',
  background: 'var(--sym-bg-elevated)',
  display: 'flex',
  flexDirection: 'column',
  pointerEvents: 'auto',
};
const mobileBar: CSSProperties = { ...mobileBarBase, padding: '24px 12px', gap: 12 };

const mobileHeading: CSSProperties = {
  fontSize: 16,
  lineHeight: '20px',
  fontWeight: 'var(--sym-weight-medium)',
  letterSpacing: '-0.32px',
  color: 'var(--ds-text-primary)',
  whiteSpace: 'nowrap',
};
const mobileCounter: CSSProperties = {
  ...mobileHeading,
  fontVariantNumeric: 'tabular-nums',
  fontFeatureSettings: '"tnum" 1',
  flexShrink: 0,
};

const mobileAlertTitle: CSSProperties = {
  fontSize: 14,
  lineHeight: '18px',
  fontWeight: 'var(--sym-weight-semibold)',
  letterSpacing: '-0.28px',
  color: 'var(--ds-text-primary)',
  whiteSpace: 'nowrap',
};
const mobileAlertReset: CSSProperties = { ...mobileAlertTitle, fontWeight: 'var(--sym-weight-regular)', whiteSpace: 'normal' };
const mobileAlertOffer: CSSProperties = {
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 'var(--sym-weight-regular)',
  letterSpacing: '-0.28px',
  color: 'var(--ds-text-primary)',
  whiteSpace: 'normal',
};
export function PersonalSaleBanner({
  narrow,
  onPackages = false,
  anchorMs,
  onDismiss,
}: {
  narrow: boolean;
  /** Desktop only: render the full-viewport blue `/packages` variant (Figma 16946-167945). Ignored on
   *  narrow, where the package picker shows the ordinary mobile strip like every other screen. */
  onPackages?: boolean;
  anchorMs: number | null;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const countdown = usePersonalSaleCountdown(anchorMs);
  // In a low/out credits state the banner leads with that alert + the offer line; otherwise the plain
  // welcome banner. `level` is already gated on the low/out-credits FT.
  const creditsState = useCreditsWidgetState();
  const copy = personalSaleBannerCopy(creditsState.level);
  // "Your daily credits come back at {time}." — daily OUT only, when the reset instant is known and ahead.
  const resetLine = useDailyCreditsResetLine(widgetStateResetInputs(creditsState));
  const isAlert = copy.variant === 'creditsAlert';
  const ctaSize = !narrow ? 'medium' : 'small';
  // The chat overlay never unmounts on navigation (it rides along so a live turn/voice call keeps
  // going) — without this, tapping the CTA from inside a fullscreen conversation changes the URL to
  // `/packages` but the fullscreen chat, still `position: fixed` over the whole viewport, keeps covering
  // it: the banner disappears (it never shows on /packages) while the screen visibly stays on chat. Every
  // other in-app link already runs this on click (`use-leave-chat.ts`) — it floats a maximized chat back
  // to the dock on wide, or minimizes it to the bubble on narrow, so the destination is actually visible.
  const leaveChat = useLeaveChatOnNavigate();
  // BI: the FT-independent credits-state fields ride both banner events (and the coexisting upgrade
  // event), one source of truth shared with the credits CTA. See `useCreditsBiFields`.
  const { logPageClickBi } = useGenericBi();
  const biFields = useCreditsBiFields();
  const basePath = useBasePath();
  const creditsData = useCredits();
  const { isFreePlan } = useCreditPlanContext();
  const meter = resolveCreditsMeter(creditsData);
  const cap = meter?.cap ?? 0;
  const used = meter?.used ?? 0;
  // The banner CTA fires its OWN click event AND the shared `upgrade_credits` (coexist by design): the
  // dedicated event measures the promo, the shared one keeps feeding the generic upgrade-intent funnel
  // (different component names, so they never merge). Both carry the same state fields. The banner is
  // Free-plan-only, so it is always the upgrade (never top-up) branch. Mirrors native.
  const onCtaClick = (event?: ClickModifiers) => {
    logPageClickBi(
      { page_name: 'personal_sale_banner', button_name: 'personal_sale_banner_cta', origin: 'personal_sale_banner' },
      { additionalFields: biFields },
    );
    logPageClickBi(
      { page_name: 'personal_sale_banner', button_name: 'upgrade_credits', origin: 'personal_sale_banner' },
      {
        additionalFields: {
          ...biFields,
          url: `${basePath}/packages`,
          plan: isFreePlan ? 'free' : 'paid',
          plan_credits: String(cap),
          remain_credits: String(Math.max(0, cap - used)),
        },
      },
    );
    // Only for a same-tab navigation — a modified/middle click opens `/packages` in a NEW tab, so the
    // CURRENT tab never navigates and its own chat-collapse still needs the sync this would otherwise
    // suppress. Before `leaveChat()`, not after — see `skipChatUrlSyncOnce` for why the order matters.
    if (routerHandlesClick(event ?? {})) skipChatUrlSyncOnce();
    leaveChat();
  };
  const handleDismiss = () => {
    logPageClickBi(
      { page_name: 'personal_sale_banner', button_name: 'personal_sale_banner_dismiss', origin: 'personal_sale_banner' },
      { additionalFields: biFields },
    );
    onDismiss();
  };
  const cta = (
    <Button color="accent" size={ctaSize} href={upgradeHref('personal_sale_banner')} onClick={onCtaClick}>
      {t('personalSale.banner.cta')}
    </Button>
  );

  if (narrow) {
    // Mobile-web has NO X — a "Close" text button in flow instead (Figma 16946-164359), mobile-web's
    // own choice rather than a native-parity one.
    const closeButton = (
      <Button variant="text" color="standard" size="small" onClick={handleDismiss}>
        {t('common.close')}
      </Button>
    );
    // The content block is CENTRED as a unit within the full-width strip — it does not stretch to fill
    // it (Figma 16946-164359: the block sizes to its own content, e.g. a 215-wide CTA well short of the
    // 339-wide bar). Close sits on the CTA's OWN row, aligned to where the row above it ends — `alignItems:
    // 'stretch'` makes every row share the block's width (set by whichever is intrinsically widest), so
    // `justify-content: space-between` on the CTA row lands Close at that same right edge, falling out of
    // the layout rather than a hardcoded offset. The low/out variant reuses this EXACT shape, with one
    // extra SemiBold alert-title line stacked above the offer text (both left-aligned in the same column,
    // so they share an edge automatically) and the offer text standing in for the plain title alongside
    // the counter.
    return (
      <div style={mobileBar}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'stretch' }}>
            {/* Figma 16946-163598: the alert title sits 4px above the offer text specifically — its OWN
                nested gap, distinct from the 12px rhythm between this block and the CTA row below. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: isAlert ? 4 : 0 }}>
              {isAlert && <span style={mobileAlertTitle}>{t(copy.titleKey)}</span>}
              {isAlert && resetLine && <span style={mobileAlertReset}>{resetLine}</span>}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                <span style={isAlert ? mobileAlertOffer : mobileHeading}>
                  {isAlert ? t('personalSale.banner.offer') : t('personalSale.banner.title')}
                </span>
                <span style={mobileCounter}>{countdown}</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              {cta}
              {closeButton}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop /packages: the full-viewport blue strip. It shows the plain welcome copy regardless of the
  // credit state — the picker is its own promotional surface and Figma gives only this one treatment
  // (no blue low/out mock exists), so it does not switch to the low/out alert copy here. The CTA is the
  // WHITE button (the `.packagesCta` class overrides the accent skin's green fill), and the light-ink X.
  if (onPackages) {
    return (
      <div style={packagesStrip}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <span style={packagesHeading}>{t('personalSale.banner.title')}</span>
          <span style={packagesCounter}>{countdown}</span>
          <Button
            color="standard"
            variant="contained"
            size="medium"
            className={styles.packagesCta}
            href={upgradeHref('personal_sale_banner')}
            onClick={onCtaClick}
          >
            {t('personalSale.banner.cta')}
          </Button>
        </div>
        <span style={packagesCloseSlot}>
          <IconButton size="tiny" variant="text" aria-label={t('personalSale.banner.dismiss')} onClick={handleDismiss}>
            <X size={16} strokeWidth={2} color="var(--ds-neutral-50)" aria-hidden />
          </IconButton>
        </span>
      </div>
    );
  }

  return (
    <div style={bar}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        {isAlert ? (
          <>
            {resetLine ? (
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={alertTitle}>{t(copy.titleKey)}</span>
                <span style={alertReset}>{resetLine}</span>
              </span>
            ) : (
              <span style={alertTitle}>{t(copy.titleKey)}</span>
            )}
            <span style={alertOffer}>{t('personalSale.banner.offer')}</span>
            <span style={alertCounter}>{countdown}</span>
          </>
        ) : (
          <>
            <span style={heading}>{t('personalSale.banner.title')}</span>
            <span style={counter}>{countdown}</span>
          </>
        )}
        {cta}
      </div>
      <span style={closeSlot}>
        <IconButton size="tiny" variant="text" aria-label={t('personalSale.banner.dismiss')} onClick={handleDismiss}>
          <X size={16} strokeWidth={2} aria-hidden />
        </IconButton>
      </span>
    </div>
  );
}
